import {
	BaseFilter,
	BaseFilterStop,
	Nights,
	OfferDateRange,
	OfferFilter,
	OfferListHeader,
	OfferListParams,
	ParamsDateRange,
	Selected,
} from '@/_store/offers/offers.types';
import { formatOskarDate } from '@/_utils/datetime/format-oskar-date';
import { convertObjectValuesToStrings } from '@/_utils/strings/convert-object-values-to-strings';
import { stringToArray } from '@/_utils/strings/string-to-array';
import { createApiURL } from '@/_utils/urls/create-api-url';
import {
	MultiChoiceModes,
	OfferListHeaderItemMode,
	TopSearchDropdownItemMode,
} from '@/new-top-search/top-search-dropdown-item/top-search-dropdown-item.types';
import { TopSearchHeaderConfig } from '@/new-top-search/top-search.config';
import { Params } from '@angular/router';
import { TransportKind, transportKindMap } from '@/offer/offer.model';
import { PriceRange } from '@/new-top-search/top-search-dropdown-item/price-filter/price-filter.component';

export const createOfferListApiParams = (selected: Selected): string => {
	const { lower, upper } = selected.offersDateRange;
	const { min, max } = selected.nights;

	const dateRangeString: ParamsDateRange = lower && upper ? `${lower},${upper}` : '';

	const paramsObj: OfferListParams = {
		adults: selected.adults,
		children: selected.children,
		boards: selected.boards,
		conveniences: selected.conveniences,
		kinds: selected.kinds,
		locations: selected.locations,
		stops: selected.stops,
		tags: selected.tags,
		nights_min: min,
		nights_max: max,
		date_range: dateRangeString,
		search: selected.search,
		status: selected.status,
		highlight_last_seats: selected.highlightLastSeats,
		transport_kind: selected.transportKind,
		products: selected.products,
		price_min: selected.price?.min * 100,
		price_max: selected.price?.max * 100,
	};

	const filteredObject: Record<string, string> = Object.fromEntries(
		Object.entries(convertObjectValuesToStrings(paramsObj)).filter(([, value]) => value !== ''),
	);

	return createApiURL(filteredObject);
};

export const findParticipantValue = (
	filterOptions: OfferFilter,
	queryParams: Params,
	type: 'adults' | 'children',
	defaultValue: number,
): number => {
	return filterOptions[type].find((v) => v === +queryParams[type])
		? +queryParams[type]
		: filterOptions[type].includes(defaultValue)
			? defaultValue
			: Math.min(...filterOptions[type]);
};

export const findNightValue = (filterOptions: OfferFilter, queryParams: Params, type: 'min' | 'max'): number => {
	return filterOptions.defaultNights.find((v) => v === +queryParams[`nights_${type}`])
		? +queryParams[`nights_${type}`]
		: Math[type](...filterOptions.nights[type]);
};

export const setSelectedValueBaseOnParams = (filterOptions: OfferFilter, queryParams: Params): Selected => {
	const children = findParticipantValue(filterOptions, queryParams, 'children', 0);
	const adults = findParticipantValue(filterOptions, queryParams, 'adults', children > 0 ? 0 : 2);

	let dateRangeArr: string[] = [];
	if (queryParams['date_range']) {
		dateRangeArr = queryParams['date_range'].split(',');
	}

	return {
		adults: adults,
		boards: stringToArray(queryParams['boards']),
		children: children,
		conveniences: stringToArray(queryParams['conveniences']),
		kinds: stringToArray(queryParams['kinds']),
		locations: stringToArray(queryParams['locations']),
		nights: {
			max: findNightValue(filterOptions, queryParams, 'max'),
			min: findNightValue(filterOptions, queryParams, 'min'),
		},
		offersDateRange: {
			lower: dateRangeArr.length ? dateRangeArr[0] : null,
			upper: dateRangeArr.length ? dateRangeArr[1] : null,
		},
		roomType: [],
		stops: stringToArray(queryParams['stops'] ?? queryParams['stop_id']),
		tags: stringToArray(queryParams['tags']),
		search: queryParams['search'] || '',
		status: Number.isNaN(+queryParams['status']) ? null : +queryParams['status'],
		highlightLastSeats: queryParams['highlight_last_seats'],
		transportKind: queryParams['transport_kind']?.split(',') ?? '',
		products: stringToArray(queryParams['products']),
		price: {
			min: queryParams['price_min'] ? parseInt(queryParams['price_min']) / 100 : null,
			max: queryParams['price_max'] ? parseInt(queryParams['price_max']) / 100 : null,
		},
	};
};

export const createQueryParams = (queryString: string): Params => {
	const queryParamsArray = queryString.slice(1).split('&');
	return queryParamsArray.reduce((acc, param) => {
		const [key, value] = param.split('=');
		(acc as Params)[key] = value;
		return acc;
	}, {});
};

export const createOfferListHeaderArray = (selected: Selected, options: OfferFilter): OfferListHeader[] => {
	return Object.keys(selected)
		.map((key) => {
			const selectedValues = selected[key as keyof Selected];
			const selectedOptions = options[key as keyof OfferFilter];
			const text = TopSearchHeaderConfig.find((v) => v.id === key)?.text;

			switch (key) {
				case OfferListHeaderItemMode.ADULTS:
				case OfferListHeaderItemMode.CHILDREN:
					if (selectedValues) {
						return createParticipantHeader(selectedValues as number, key);
					}
					break;

				case OfferListHeaderItemMode.NIGHTS:
					return createNightsHeader(selectedValues as Nights, key, options, text);
				case OfferListHeaderItemMode.DATE_RANGE:
					return createDateRangeHeader(selectedValues as OfferDateRange, key, text);
				case OfferListHeaderItemMode.STOPS:
					return createMultiChoiceHeader(
						selectedValues as number[],
						(selectedOptions as BaseFilterStop[]).filter((v) =>
							selected.transportKind.length ? selected.transportKind.includes(v.kind) : v,
						),
						key,
						text,
					);
				case OfferListHeaderItemMode.PRICE:
					const { min, max } = selectedValues as PriceRange;
					if (selectedValues && (min || max)) {
						return {
							key: key,
							name: text,
							value: getPriceFilterString(min, max),
							defaultOption: false,
						};
					}
					break;
				case OfferListHeaderItemMode.SEARCH:
					if (selectedValues) {
						return {
							key: key,
							name: text,
							value: selectedValues as string,
							defaultOption: false,
						};
					}
					break;
				case OfferListHeaderItemMode.TRANSPORT_KIND:
					const transportArray = Object.entries(transportKindMap).map(([id, name]) => ({
						id: id as TransportKind,
						name,
					}));

					return createMultiChoiceHeader(
						selectedValues as number[],
						transportArray as unknown as BaseFilter[],
						key,
						text,
					);
				default:
					if (!(key as TopSearchDropdownItemMode)) {
						return;
					}

					if (MultiChoiceModes.includes(key as TopSearchDropdownItemMode)) {
						return createMultiChoiceHeader(
							selectedValues as number[],
							selectedOptions as BaseFilter[],
							key,
							text,
						);
					}
					break;
			}
		})
		.filter((v) => v)
		.sort((a, b) => customListHeaderSort(a, b));
};

export const createParticipantHeader = (selectedValues: number, key: string): OfferListHeader => ({
	key: key,
	name: key === OfferListHeaderItemMode.ADULTS ? 'Dorośli' : 'Dzieci (0-12 lat)',
	value: selectedValues as number,
	defaultOption: key === OfferListHeaderItemMode.ADULTS, // if default option is true it's not removeable
});

export const createNightsHeader = (
	selectedValues: Nights,
	key: string,
	options: OfferFilter,
	text: string,
): OfferListHeader => {
	const { min: nightsMin, max: nightsMax } = selectedValues as Nights;
	let defaultOption = false;
	if (nightsMin === Math.min(...options.defaultNights) && nightsMax === Math.max(...options.defaultNights)) {
		defaultOption = true;
	}
	if (nightsMin && nightsMax) {
		return {
			key: key,
			name: text,
			value: nightsMin === nightsMax ? nightsMin : `${nightsMin} - ${nightsMax}`,
			defaultOption: defaultOption,
		};
	}
};

export const createDateRangeHeader = (selectedValues: OfferDateRange, key: string, text: string): OfferListHeader => {
	const { lower, upper } = selectedValues;
	if (lower && upper) {
		return {
			key: key,
			name: text,
			value: `${formatOskarDate(new Date(lower))} - ${formatOskarDate(new Date(upper))}`,
			defaultOption: false,
		};
	}
};

export const createMultiChoiceHeader = (
	selectedValues: number[],
	selectedOptions: BaseFilter[],
	key: string,
	text: string,
): OfferListHeader => {
	const multiChoiceValues = selectedOptions.filter((v) => selectedValues.includes(v.id));

	if (multiChoiceValues.length) {
		return {
			key: key,
			name: text,
			value: multiChoiceValues.map((v) => v.name).join(', '),
			defaultOption: false,
			options: multiChoiceValues.map((v) => ({ id: v.id, name: v.name })),
		};
	}
};

export const customListHeaderSort = (a: OfferListHeader, b: OfferListHeader): number => {
	const sortingOrder = [
		OfferListHeaderItemMode.ADULTS,
		OfferListHeaderItemMode.CHILDREN,
		OfferListHeaderItemMode.NIGHTS,
		OfferListHeaderItemMode.DATE_RANGE,
	] as string[];
	const indexA = sortingOrder.indexOf(a.key);
	const indexB = sortingOrder.indexOf(b.key);

	if (indexA !== -1 && indexB !== -1) {
		return indexA - indexB;
	}

	if (indexA !== -1) {
		return -1;
	}

	if (indexB !== -1) {
		return 1;
	}

	return a.key.localeCompare(b.key);
};

export const findHeaderValue = (headers: OfferListHeader[], key: string): string | number =>
	headers.find((v) => v.key === key)?.value;

export const getOffsetFromNext = (next: string | null): number | null => {
	const params = new URLSearchParams(next);
	const offset = params.get('offset');
	return Number(offset) ?? null;
};

/**
 * Returns the corresponding TransportKind enum key for a given transport name.
 *
 * @param name - The human-readable name of the transport (e.g., "Samolot", "Autobus").
 * @returns The matching TransportKind enum value (e.g., TransportKind.PLANE).
 * @throws Will throw an error if the name does not match any known transport kind.
 *
 * @example
 * ```ts
 * const kind = getTransportKindByNameOrThrow('Samolot');
 * // kind === TransportKind.PLANE
 * ```
 */
export function getTransportKindByNameOrThrow(name: string): TransportKind {
	const kind = (Object.entries(transportKindMap) as [TransportKind, string][]).find(
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		([_, value]) => value === name,
	)?.[0];

	if (!kind) {
		throw new Error(`Unknown transport name: ${name}`);
	}

	return kind;
}

/**
 * @param min - minimum price filter
 * @param max - maximum price filter
 * @returns proper name based on min/max price filter
 */
export const getPriceFilterString = (min: number, max: number): string => {
	if (!min && !max) {
		return '';
	}

	if (min && !max) {
		return ` > ${min}zł/os `;
	}

	if (!min && max) {
		return ` < ${max}zł/os `;
	}

	return `${!!min ? min : 0}zł/os - ${!!max ? max : 0}zł/os`;
};
